import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const Header = () => {
  const { user, logout, refreshCredits } = useAuth(); // ✅ Added refreshCredits
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // ✅ Refresh credits when component mounts
  useEffect(() => {
    if (user?.id) {
      refreshCredits();
    }
  }, [user?.id, refreshCredits]);

  // ✅ Auto-refresh credits every 30 seconds
  useEffect(() => {
    if (!user?.id) return;

    const interval = setInterval(() => {
      refreshCredits();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [user?.id, refreshCredits]);

  const handleLogout = () => {
    // Clear chat history and first question status on logout
    if (user?.id) {
      localStorage.removeItem(`astroguru_chat_${user.id}`);
      localStorage.removeItem(`astroguru_first_question_${user.id}`);
      localStorage.removeItem(`astroguru_draft_${user.id}`);
      localStorage.removeItem(`astroguru_short_response_${user.id}`);
    }
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  const handleGetCredits = () => {
    navigate('/credits');
    setShowUserMenu(false); // ✅ Close menu when navigating
  };

  const clearChatHistory = () => {
    const confirmClear = window.confirm('Are you sure you want to clear all chat history? This will reload your cosmic welcome and daily horoscope.');
    if (confirmClear) {
      if (user?.id) {
        localStorage.removeItem(`astroguru_chat_${user.id}`);
        localStorage.removeItem(`astroguru_first_question_${user.id}`);
        localStorage.removeItem(`astroguru_draft_${user.id}`);
      }
      // Refresh the page to reload chat
      window.location.reload();
    }
  };

  const handleProfileClick = () => {
    console.log('Profile clicked');
    setShowUserMenu(false);
    // navigate('/profile'); // Uncomment when you create profile page
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  // Responsive: show menu button on small screens, header actions row on large screens
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 900);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when clicking outside (for dropdown)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-container') && !event.target.closest('.header-menu-btn')) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  return (
    <header className="app-header">
      <div className="header-left">
        <span className="logo-icon">⭐</span>
        <h1 className="logo-text">ASTROGURU</h1>
      </div>
      {/* Large screens: show header-right row */}
      {!isMobile && (
        <div className="header-right">
          <div className="credits-display" onClick={handleGetCredits}>
            <span className="credits-icon">💎</span>
            <span className={`credits-count ${user?.credits < 10 ? 'low-credits' : ''}`}>{user?.credits || 0} Credits</span>
            {user?.credits < 10 && <span className="warning-icon">⚠️</span>}
          </div>
          {location.pathname === '/chat' && (
            <button className="clear-chat-btn" onClick={clearChatHistory} title="Clear chat and reload cosmic guidance">
              <span>🗑️</span>
              <span>Refresh</span>
            </button>
          )}
          {user && (
            <div className="user-menu-container">
              <div className="user-info" onClick={toggleUserMenu}>
                <div className="user-avatar">
                  {user.picture ? (
                    <img 
                      src={user.picture} 
                      alt="User" 
                      className="avatar-img"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = (user.full_name || user.name) 
                          ? (user.full_name || user.name).split(' ').map(n => n[0]).join('').substr(0, 2).toUpperCase() 
                          : '👤';
                      }}
                    />
                  ) : (
                    (user.full_name || user.name) ? (user.full_name || user.name).split(' ').map(n => n[0]).join('').substr(0, 2).toUpperCase() : '👤'
                  )}
                </div>
                <span className="user-name">{user.full_name || user.name}</span>
                <span className="dropdown-arrow">▼</span>
              </div>
              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="user-dropdown-menu">
                  <div className="dropdown-header">
                    <div className="dropdown-avatar">
                      {user.picture ? (
                        <img 
                          src={user.picture} 
                          alt="User" 
                          className="dropdown-avatar-img"
                        />
                      ) : (
                        <div className="dropdown-avatar-placeholder">
                          {(user.full_name || user.name) ? (user.full_name || user.name).split(' ').map(n => n[0]).join('').substr(0, 2).toUpperCase() : '👤'}
                        </div>
                      )}
                    </div>
                    <div className="dropdown-user-info">
                      <div className="dropdown-user-name">{user.full_name || user.name}</div>
                      <div className="dropdown-user-email">{user.email}</div>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item credits-display" style={{justifyContent:'flex-start', background:'none', border:'none', boxShadow:'none', color:'#FFD700', fontWeight:'bold'}}>
                    <span className="credits-icon">💎</span>
                    <span className={`credits-count ${user?.credits < 10 ? 'low-credits' : ''}`}>{user?.credits || 0} Credits</span>
                    {user?.credits < 10 && <span className="warning-icon">⚠️</span>}
                  </div>
                  <button className="dropdown-item" onClick={handleProfileClick}>
                    <span className="dropdown-icon">👤</span>
                    <span>Profile</span>
                  </button>
                  <button className="dropdown-item" onClick={handleGetCredits}>
                    <span className="dropdown-icon">💎</span>
                    <span>Buy Credits</span>
                  </button>
                  {location.pathname === '/chat' && (
                    <button className="dropdown-item" onClick={clearChatHistory}>
                      <span>🗑️</span>
                      <span>Refresh</span>
                    </button>
                  )}
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <span className="dropdown-icon">🚪</span>
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Small screens: show menu button */}
      {isMobile && user && (
        <div className="user-menu-container">
          <button className="header-menu-btn" onClick={toggleUserMenu} aria-label="Open menu">
            <span className="dropdown-icon">☰</span>
            <span>Menu</span>
          </button>
          {showUserMenu && (
            <div className="user-dropdown-menu mobile-menu">
              <div className="dropdown-header">
                <div className="dropdown-avatar">
                  {user.picture ? (
                    <img 
                      src={user.picture} 
                      alt="User" 
                      className="dropdown-avatar-img"
                    />
                  ) : (
                    <div className="dropdown-avatar-placeholder">
                      {(user.full_name || user.name) ? (user.full_name || user.name).split(' ').map(n => n[0]).join('').substr(0, 2).toUpperCase() : '👤'}
                    </div>
                  )}
                </div>
                <div className="dropdown-user-info">
                  <div className="dropdown-user-name">{user.full_name || user.name}</div>
                  <div className="dropdown-user-email">{user.email}</div>
                </div>
              </div>
              <div className="dropdown-divider"></div>
              <div className="dropdown-item credits-display" style={{justifyContent:'flex-start', background:'none', border:'none', boxShadow:'none', color:'#FFD700', fontWeight:'bold'}}>
                <span className="credits-icon">💎</span>
                <span className={`credits-count ${user?.credits < 10 ? 'low-credits' : ''}`}>{user?.credits || 0} Credits</span>
                {user?.credits < 10 && <span className="warning-icon">⚠️</span>}
              </div>
              <button className="dropdown-item" onClick={handleProfileClick}>
                <span className="dropdown-icon">👤</span>
                <span>Profile</span>
              </button>
              <button className="dropdown-item" onClick={handleGetCredits}>
                <span className="dropdown-icon">💎</span>
                <span>Buy Credits</span>
              </button>
              {location.pathname === '/chat' && (
                <button className="dropdown-item" onClick={clearChatHistory}>
                  <span>🗑️</span>
                  <span>Refresh</span>
                </button>
              )}
              <button className="dropdown-item logout-item" onClick={handleLogout}>
                <span className="dropdown-icon">🚪</span>
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
